import { Option, isErr, none, some, tryFn } from "@jace/api";
import { ZodSchema } from "zod";
import { CachedEntry, Serialziable } from "./cache";
import { LRUCache } from "./lru-cache";

const WEEK = 7 * 24 * 60 * 60 * 1000;

type CacheType = "localstorage" | "memory";

interface StorageSetterOptions {
	ttl?: number;
}

interface StorageAdapater {
	set(key: string, value: Serialziable): Promise<boolean>;
	get<TSchema extends ZodSchema>(
		key: string,
		schema: TSchema,
		options?: StorageSetterOptions,
	): Promise<Option<ReturnType<TSchema["parse"]>>>;
}

class InMemoryStorageAdapater implements StorageAdapater {
	public cache: LRUCache<string, CachedEntry<any>>;

	constructor(capacity = 100) {
		this.cache = new LRUCache(capacity);
	}

	public async set(
		key: string,
		value: Serialziable,
		options?: StorageSetterOptions,
	): Promise<boolean> {
		try {
			const ttl = options?.ttl ?? 0;

			this.cache.set(key, {
				id: key,
				expiresAt: Date.now() + ttl,
				data: value,
			});

			return true;
		} catch (e) {
			return false;
		}
	}

	public async get<TSchema extends ZodSchema>(
		key: string,
		schema: TSchema,
	): Promise<Option<ReturnType<TSchema["parse"]>>> {
		const entry = this.cache.get(key);

		if (!entry) {
			return none();
		}

		if (entry.expiresAt < Date.now()) {
			console.log("expired!");
			this.cache.delete(key);
			return none();
		}

		const result = schema.safeParse(entry.data);

		if (result.success) {
			return some(result.data);
		}

		return none();
	}

	public toString(): string {
		return this.cache.toJSON();
	}
}

class LocalStorageAdapter implements StorageAdapater {
	private _entries: InMemoryStorageAdapater;

	constructor(
		capacity = 100,
		public readonly namespace = "jace-storage",
	) {
		this._entries = new InMemoryStorageAdapater(capacity);

		if (!localStorage) {
			throw new Error("LocalStorage is not available");
		}

		const data = localStorage.getItem(namespace);

		if (data) {
			this._entries.cache.fromJSON(data);
		}
	}

	public async set(
		key: string,
		value: Serialziable,
		options?: StorageSetterOptions,
	): Promise<boolean> {
		const opts = { ttl: WEEK, ...options };

		try {
			await this._entries.set(key, value, opts);
			this.persist();

			return true;
		} catch (_e) {
			return false;
		}
	}

	public async get<TSchema extends ZodSchema>(
		key: string,
		schema: TSchema,
	): Promise<Option<ReturnType<TSchema["parse"]>>> {
		return this._entries.get(key, schema);
	}

	private persist(): boolean {
		const result = tryFn(() =>
			localStorage.setItem(this.namespace, this._entries.toString()),
		);

		if (isErr(result)) {
			return false;
		}

		return true;
	}
}

export class Storage {
	private _adapter: StorageAdapater;

	constructor(
		public readonly type: CacheType = "memory",
		public readonly capacity = 100,
	) {
		this._adapter = this.getAdapterFromType(type);
	}

	public async set(key: string, value: Serialziable): Promise<boolean> {
		return this._adapter.set(key, value);
	}

	public async get<TSchema extends ZodSchema>(
		key: string,
		schema: TSchema,
	): Promise<Option<ReturnType<TSchema["parse"]>>> {
		return this._adapter.get(key, schema);
	}

	private getAdapterFromType(type: CacheType): StorageAdapater {
		switch (type) {
			case "localstorage":
				return new LocalStorageAdapter(this.capacity);
			default:
				return new InMemoryStorageAdapater(this.capacity);
		}
	}
}
